import React, { useEffect, useRef } from 'react';
import { MoreHoriz as MoreHorizIcon } from '@material-ui/icons';
import Card from '../../../common/card/Card';
import CardContent from '../../../common/card/CardContent';
import CardTitle from '../../../common/card/CardTitle';
import InlineForm from '../../../common/inline/InlineForm';
import InlineFormik from '../../../common/inline/InlineFormik';
import { useUpdateChangeOrder } from '../ChangeOrder.hook';
import {
  BaseChangeOrderCardProps,
  ChangeOrder
} from '../ChangeOrder.interface';
import InlineHtml from '../../../common/inline/InlineHtml';
import { InlineFileValue } from '../../../common/inline/InlineFile';
import { useUpdateTask } from '../../tasks/Task.hooks';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';
import InlineDatePicker from '../../../common/inline/InlineDatePicker';

export interface ChangeOrderOtherCardProps extends BaseChangeOrderCardProps {
  changeOrder: ChangeOrder;
  setSuccessMessage: (message: string) => void;
  setErrorMessage: (message: string) => void;
}

const OrderOtherCardFields = [
  {
    name: 'documentInteractionDescription',
    label: 'Assess interaction with other documents in the QMS'
  },
  {
    name: 'ehsAssessmentDescription',
    label: 'Assess EHS'
  },
  {
    name: 'regulationAssessmentOther',
    label: 'Assess change with respect to applicable regulations as needed'
  }
];

export const ChangeOrderOtherCard: React.FC<
  ChangeOrderOtherCardProps
> = props => {
  const { changeOrder, onChange, setSuccessMessage, setErrorMessage } = props;
  const onChangeRef = useRef(onChange);
  const { updatedChangeOrder, isLoading, error, updateChangeOrder } =
    useUpdateChangeOrder(changeOrder.id);
  const {
    updatedTask,
    updateError: updateTaskError,
    updateTask
  } = useUpdateTask(changeOrder.task.id || 0);
  const { isFieldDisabled } = useTaskEntityFields(changeOrder.task);

  const initialValues: Partial<ChangeOrder> = {
    documentInteractionDescription:
      changeOrder.documentInteractionDescription || '',
    ehsAssessmentDescription: changeOrder.ehsAssessmentDescription || '',
    regulationAssessmentOther: changeOrder.regulationAssessmentOther || '',
    commentsOther: changeOrder.commentsOther || '',
    otherAssessmentDueDate: changeOrder.otherAssessmentDueDate,
    otherAssessmentCompletedDate: changeOrder.otherAssessmentCompletedDate
  };

  useEffect(() => {
    if (updatedChangeOrder?.id) {
      setSuccessMessage('Change Order successfully updated');
      onChangeRef.current?.(updatedChangeOrder);
    }
  }, [setSuccessMessage, updatedChangeOrder]);

  useEffect(() => {
    if (Boolean(error || updateTaskError))
      setErrorMessage('There was an error saving');
  }, [setErrorMessage, error, updateTaskError]);

  useEffect(() => {
    if (updatedTask) {
      changeOrder.task = Object.assign(changeOrder.task || {}, updatedTask);
      onChange?.(changeOrder);
    }
  }, [updatedTask]);

  const handleOnChangeAttachment = (attachments: InlineFileValue[]) => {
    updateTask({ attachments });
    if (changeOrder.task) {
      changeOrder.task.attachments = attachments;
    }
    onChange?.(changeOrder);
  };

  return (
    <InlineFormik
      initialValues={initialValues}
      strict={false}
      isSubmitting={isLoading}
      error={error}
      onSubmit={(updatedValue: Partial<ChangeOrder>) => {
        const dto = { ...updatedValue };
        updateChangeOrder(dto);
      }}
    >
      <Card>
        <CardTitle>
          <MoreHorizIcon />
          Other Assessment
        </CardTitle>
        <CardContent padding='large'>
          <InlineForm>
            {OrderOtherCardFields.map((field, key) => (
              <InlineHtml
                key={key}
                name={field.name}
                label={field.label}
                disabled={isFieldDisabled(field.name)}
                attachments={changeOrder.task.attachments || []}
                onChangeAttachments={handleOnChangeAttachment}
              />
            ))}
            <InlineDatePicker
              grid={{ lg: 6, md: 6 }}
              clearable
              name='otherAssessmentDueDate'
              label='Due Date'
              placeholder='Enter Due Date'
              disabled={isFieldDisabled('otherAssessmentDueDate')}
              autoCloseOnSelect
            />
            <InlineDatePicker
              grid={{ lg: 6, md: 6 }}
              clearable
              name='otherAssessmentCompletedDate'
              label='Completed Date'
              placeholder='Enter Completed Date'
              disabled={isFieldDisabled('otherAssessmentCompletedDate')}
              autoCloseOnSelect
            />
            <InlineHtml
              name='commentsOther'
              label='Comments'
              disabled={isFieldDisabled('commentsOther')}
              helperText='Provide any additional information here'
              attachments={changeOrder.task.attachments || []}
              onChangeAttachments={handleOnChangeAttachment}
            />
          </InlineForm>
        </CardContent>
      </Card>
    </InlineFormik>
  );
};
